/**
 * DocumentTable — presentational list of documents with row actions.
 * Mirrors the layout idioms of CustomerTable (card-wrapped table, skeleton,
 * empty state, dark-mode tokens).
 */

import { FileText, Download, Trash2, ShieldCheck, Eye, RefreshCw } from 'lucide-react'
import type { Document } from '@/types/document.types'
import {
  DOCUMENT_STATUS_LABEL,
  labelForCategory,
  formatFileSize,
} from '@/types/document.types'

interface DocumentTableProps {
  documents: Document[]
  isLoading?: boolean
  selectedIds: string[]
  onToggleSelect: (id: string) => void
  onSelectAll: (ids: string[]) => void
  onView?: (doc: Document) => void
  onDownload?: (doc: Document) => void
  onVerify?: (doc: Document) => void
  onReplace?: (doc: Document) => void
  onDelete?: (doc: Document) => void
}

const STATUS_BADGE: Record<string, string> = {
  verified: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  uploaded: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  processing: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  pending: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  archived: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500',
}

function StatusBadge({ status }: { status: Document['status'] }) {
  const cls = STATUS_BADGE[status] ?? STATUS_BADGE.pending
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {DOCUMENT_STATUS_LABEL[status] ?? status}
    </span>
  )
}

function formatDate(value?: string | null): string {
  if (!value) return '—'
  const date = new Date(value)
  if (isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

export default function DocumentTable({
  documents,
  isLoading,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onView,
  onDownload,
  onVerify,
  onReplace,
  onDelete,
}: DocumentTableProps) {
  if (isLoading) {
    return (
      <div className="card card-elevated p-6 space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-10 rounded-lg bg-surface-overlay/60 animate-pulse" />
        ))}
      </div>
    )
  }

  if (documents.length === 0) {
    return (
      <div className="card card-elevated p-12 text-center">
        <div className="w-16 h-16 bg-surface-overlay rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="w-8 h-8 text-text-tertiary" />
        </div>
        <p className="text-lg font-medium text-text-primary mb-1">No documents</p>
        <p className="text-text-secondary">Upload a document to get started.</p>
      </div>
    )
  }

  const allSelected = documents.length > 0 && documents.every((d) => selectedIds.includes(d.id))

  return (
    <div className="card card-elevated overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-surface-overlay text-left text-text-secondary">
              <th className="p-3 w-10">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={allSelected}
                  onChange={(e) => onSelectAll(e.target.checked ? documents.map((d) => d.id) : [])}
                  aria-label="Select all documents"
                />
              </th>
              <th className="p-3 font-medium">Name</th>
              <th className="p-3 font-medium">Category</th>
              <th className="p-3 font-medium">AY</th>
              <th className="p-3 font-medium">Status</th>
              <th className="p-3 font-medium">Size</th>
              <th className="p-3 font-medium">Uploaded</th>
              <th className="p-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((doc) => (
              <tr
                key={doc.id}
                className="border-b border-surface-overlay/60 hover:bg-surface-overlay/40 transition-colors"
              >
                <td className="p-3">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={selectedIds.includes(doc.id)}
                    onChange={() => onToggleSelect(doc.id)}
                    aria-label={`Select ${doc.name}`}
                  />
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onView?.(doc)}
                      className="flex items-center gap-2 text-text-primary hover:text-primary-600 font-medium text-left"
                    >
                      <FileText className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                      <span className="truncate max-w-[18rem]">{doc.name}</span>
                    </button>
                    {doc.version > 1 && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary-100 dark:bg-primary-950/40 text-primary-700 dark:text-primary-300">
                        v{doc.version}
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-3 text-text-secondary">{labelForCategory(doc.category)}</td>
                <td className="p-3">
                  {doc.assessmentYear ? (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                      AY {doc.assessmentYear}
                    </span>
                  ) : (
                    <span className="text-text-tertiary">—</span>
                  )}
                </td>
                <td className="p-3">
                  <StatusBadge status={doc.status} />
                </td>
                <td className="p-3 text-text-secondary">{formatFileSize(doc.fileSize)}</td>
                <td className="p-3 text-text-secondary">{formatDate(doc.createdAt)}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    {onDownload && (
                      <button
                        type="button"
                        onClick={() => onDownload(doc)}
                        className="btn btn-ghost btn-sm btn-square"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    )}
                    {onReplace && (
                      <button
                        type="button"
                        onClick={() => onReplace(doc)}
                        className="btn btn-ghost btn-sm btn-square text-text-tertiary hover:text-primary-600"
                        title="Replace / New Version"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    )}
                    {onVerify && !doc.isVerified && (
                      <button
                        type="button"
                        onClick={() => onVerify(doc)}
                        className="btn btn-ghost btn-sm btn-square text-green-600"
                        title="Verify"
                      >
                        <ShieldCheck className="w-4 h-4" />
                      </button>
                    )}
                    {onView && (
                      <button
                        type="button"
                        onClick={() => onView(doc)}
                        className="btn btn-ghost btn-sm btn-square"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    {onDelete && (
                      <button
                        type="button"
                        onClick={() => onDelete(doc)}
                        className="btn btn-ghost btn-sm btn-square text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
